import { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col } from 'antd';
import styles from './RoomModal.module.css';
import apiService from '../api/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { Option } = Select;

const RoomModal = ({
  isOpen,
  onClose,
  onSave,
  onEdit,
  existingItem,
  hotelId,
  role,
}) => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [form] = Form.useForm();
  const [isHousekeeping, setIsHousekeeping] = useState(false);

  useEffect(() => {
    setIsHousekeeping(role === 'housekeeping');
  }, []);

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const fetchRoomTypes = async () => {
    try {
      const response = await apiService.get(`/room-types/?hotel=${hotelId}`);
      setRoomTypes(response);
    } catch (error) {
      console.log(error);
    }
  };

  // Prefill form fields when editing
  useEffect(() => {
    if (existingItem) {
      form.setFieldsValue({
        roomTypeId: existingItem.room_type?.id,
        roomNumber: existingItem.room_number || '',
        bedType: existingItem.bed_type || '',
        roomFloor: existingItem.room_floor ?? '',
        dealPrice: existingItem.deal_price || 0,
        roomFacility: existingItem.room_facility || '',
        roomStatus: existingItem.status || '',
        hkStatus: existingItem.hk_status || '',
      });
    }
  }, [existingItem, form]);

  const handleSaveAndEdit = async values => {
    const newRoom = isHousekeeping
      ? {
          hk_status: values.hkStatus,
          hotel: hotelId,
        }
      : {
          room_type_id: values.roomTypeId,
          room_number: values.roomNumber,
          bed_type: values.bedType,
          room_floor: values.roomFloor,
          room_facility: values.roomFacility,
          status: values.roomStatus.toLowerCase(),
          hk_status: values.hkStatus,
          hotel: hotelId,
        };

    try {
      if (existingItem) {
        const response = await apiService.patch(
          `/rooms/${existingItem.id}/?${hotelId}`,
          newRoom
        );

        toast.success('Room updated successfully!');
        onEdit(response);
        onClose();
      } else {
        const response = await apiService.post(`/rooms/?${hotelId}`, newRoom);
        toast.success('Room added successfully!');
        onSave(response);
        form.resetFields();
        onClose();
      }
    } catch (error) {
      console.error('Error in saving the room:', error);
      toast.error('Failed to save room! Please try again.');
    }
  };

  return (
    <Modal
      title={existingItem ? 'Edit Room' : 'Add New Room'}
      open={isOpen}
      onCancel={onClose}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleSaveAndEdit}>
        <Row gutter={16}>
          {isHousekeeping ? (
            <Col xs={24}>
              <Form.Item
                label="Housekeeping Status"
                name="hkStatus"
                rules={[
                  {
                    required: true,
                    message: 'Please select housekeeping status',
                  },
                ]}
              >
                <Select placeholder="Select housekeeping status">
                  <Option value="clean">Clean</Option>
                  <Option value="dirty">Dirty</Option>
                  <Option value="in_progress">In-progress</Option>
                  <Option value="out_of_order">Out Of Order</Option>
                </Select>
              </Form.Item>
            </Col>
          ) : (
            <>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Room Type"
                  name="roomTypeId"
                  rules={[{ required: true, message: 'Please select a room' }]}
                >
                  <Select placeholder="Select room type" allowClear>
                    {roomTypes.map(roomType => (
                      <Option key={roomType.id} value={roomType.id}>
                        {roomType.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Room Number"
                  name="roomNumber"
                  rules={[
                    { required: true, message: 'Please enter the room number' },
                    {
                      pattern: /^[0-9]+$/,
                      message: 'Room number must be a valid positive number',
                    },
                  ]}
                >
                  <Input placeholder="Enter room number" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Bed Type"
                  name="bedType"
                  rules={[
                    { required: true, message: 'Please enter the bed type' },
                    {
                      pattern: /^[a-zA-Z\s]+$/,
                      message: 'Bed type must only contain letters and spaces',
                    },
                  ]}
                >
                  <Input placeholder="e.g., single bed" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Room Floor"
                  name="roomFloor"
                  rules={[
                    { required: true, message: 'Please select the room floor' },
                  ]}
                >
                  <Select placeholder="Select room floor">
                    <Option value={0}>Ground</Option>
                    <Option value={1}>First</Option>
                    <Option value={2}>Second</Option>
                    <Option value={3}>Third</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Facility"
                  name="roomFacility"
                  rules={[
                    { required: true, message: 'Please enter room facility' },
                    {
                      pattern: /^[a-zA-Z\s]+$/,
                      message:
                        'Room facility must only contain letters and spaces',
                    },
                  ]}
                >
                  <Input placeholder="Enter room facility" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Room Status"
                  name="roomStatus"
                  rules={[
                    { required: true, message: 'Please select room status' },
                  ]}
                >
                  <Select placeholder="Select room status">
                    <Option value="available">Available</Option>
                    <Option value="occupied">Occupied</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Housekeeping Status"
                  name="hkStatus"
                  rules={[
                    {
                      required: true,
                      message: 'Please select housekeeping status',
                    },
                  ]}
                >
                  <Select placeholder="Select housekeeping status">
                    <Option value="clean">Clean</Option>
                    <Option value="dirty">Dirty</Option>
                    <Option value="in_progress">In-progress</Option>
                    <Option value="out_of_order">Out of Order</Option>
                  </Select>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <div className={styles.buttonGroup}>
              <Button onClick={onClose} type="default">
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default RoomModal;
